import { Component, Input, OnInit } from '@angular/core'
import { FormBuilder, FormGroup } from '@angular/forms'
import { Merchant } from '@parkupp/core'
import { MerchantService } from '../../../services/merchant.service'
import { ToastrService } from 'ngx-toastr'

@Component({
    selector: 'app-merchant-branding-form',
    templateUrl: './merchant-branding-form.component.html',
    styleUrls: ['./merchant-branding-form.component.scss'],
})
export class MerchantBrandingFormComponent implements OnInit {
    @Input() merchantKey: string

    brandingForm: FormGroup
    merchant: Merchant | null = null
    loading = false
    selectedLogoFile: File | null = null

    constructor(private formBuilder: FormBuilder, private merchantService: MerchantService, private toastr: ToastrService) {
        this.brandingForm = this.formBuilder.group({
            customHeading: [''],
        })
    }

    ngOnInit(): void {
        this.loadMerchant()
    }

    loadMerchant(): void {
        if (!this.merchantKey) return

        this.merchantService.subscribeToMerchant(this.merchantKey).subscribe((document) => {
            this.merchant = new Merchant(document)
            this.merchant.$key = this.merchantKey
            console.log('Branding form - Merchant loaded:', {
                merchantKey: this.merchantKey,
                companyName: this.merchant.companyName,
                logoUrl: this.merchant.logoUrl,
            })
            this.loadBrandingSettings()
        })
    }

    loadBrandingSettings(): void {
        if (!this.merchant) return

        // Load existing branding settings from merchant data
        this.brandingForm.patchValue({
            customHeading: this.merchant.customHeading || '',
        })
    }

    onLogoSelected(event: any): void {
        const file = event.target.files[0]
        if (file) {
            // Validate file type
            if (!file.type.startsWith('image/')) {
                this.toastr.error('Please select a valid image file')
                return
            }

            // Validate file size (5MB limit)
            if (file.size > 5 * 1024 * 1024) {
                this.toastr.error('Image must be less than 5MB')
                return
            }

            this.selectedLogoFile = file
        }
    }

    async uploadLogo(): Promise<string | null> {
        if (!this.selectedLogoFile || !this.merchant) return null

        try {
            // Use the MerchantService upload method (following parking service pattern)
            return await this.merchantService.uploadWhitelabelLogo(this.merchant.$key!, this.selectedLogoFile)
        } catch (error) {
            console.error('Error uploading logo:', error)
            throw error
        }
    }

    async saveSettings(): Promise<void> {
        if (!this.merchant || this.brandingForm.invalid) return

        this.loading = true

        try {
            let logoUrl = this.merchant.logoUrl

            // Upload new logo if selected
            if (this.selectedLogoFile) {
                const uploadedUrl = await this.uploadLogo()
                if (uploadedUrl) {
                    logoUrl = uploadedUrl
                    console.log('New logo uploaded:', uploadedUrl)
                }
            }

            // Prepare update data
            const updateData: any = {
                customHeading: this.brandingForm.value.customHeading || null,
            }

            if (logoUrl) {
                updateData.logoUrl = logoUrl
            }

            console.log('Updating merchant with data:', updateData)

            // Update merchant
            await this.merchantService.updateMerchant(updateData, this.merchant.$key!)
            console.log('Merchant updated successfully in database')

            // Update local merchant object with all changes including new logo URL
            Object.assign(this.merchant, updateData)

            // Explicitly update the logo URL if it was uploaded
            if (logoUrl && logoUrl !== this.merchant.logoUrl) {
                this.merchant.logoUrl = logoUrl
                console.log('Local merchant logoUrl updated to:', logoUrl)
            }

            this.toastr.success('Branding settings saved successfully!')
            this.selectedLogoFile = null

            // Reset file input
            const fileInput = document.querySelector('input[type="file"]') as HTMLInputElement
            if (fileInput) fileInput.value = ''
        } catch (error) {
            console.error('Error saving branding settings:', error)
            this.toastr.error('Failed to save settings. Please try again.')
        } finally {
            this.loading = false
        }
    }

    resetForm(): void {
        this.brandingForm.reset({
            customHeading: '',
        })
        this.selectedLogoFile = null

        const fileInput = document.querySelector('input[type="file"]') as HTMLInputElement
        if (fileInput) fileInput.value = ''
    }

    async removeLogo(): Promise<void> {
        if (!this.merchant) return

        if (confirm('Are you sure you want to remove the logo?')) {
            this.loading = true

            try {
                // Update merchant to remove logo
                await this.merchantService.updateMerchant({ logoUrl: null }, this.merchant.$key!)

                // Update local merchant object
                this.merchant.logoUrl = undefined

                this.toastr.success('Logo removed successfully!')
            } catch (error) {
                console.error('Error removing logo:', error)
                this.toastr.error('Failed to remove logo. Please try again.')
            } finally {
                this.loading = false
            }
        }
    }
}
